
import { ControlledAssetInput } from '@cms/components/data/FileInput';
import { SystemMultiTab } from '@cms/components/data/MultiTab';
import { ControlledSystemSwitch } from '@cms/components/data/Switch';
import { ControlledSystemTextInput } from '@cms/components/data/TextInput';
import { FormActions, FormWrapper } from '@cms/components/forms/FormContainer';
import { SystemBackdrop } from '@cms/components/ui/Backdrop';
import { WrappedLoader } from '@cms/components/ui/Loader';
import { useSnackbar } from '@cms/providers/SnackbarProvider';
import api from '@cms/utils/api';
import { renderRouteOptions, Routes } from '@cms/utils/routes';
import { ISystemBannersConfigurations, ISystemDLRMentionsConfigurations, ISystemDLRXPostsConfigurations, ISystemTopBarConfigurations, TAssetMetadata } from '@dl/declarations';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import PageContainer from '../../components/ui/PageContainer';

const TOP_BANNER_LIMITS = {
  MOBILE_CONTENT_MAX_LENGTH: 50,
  // DESKTOP_CONTENT_MAX_LENGTH: 115,
  DESKTOP_CONTENT_MAX_LENGTH: 150,
}

const TopBarConfiguration = () => {
  const { showSnackbar } = useSnackbar()

  const query = useQuery({
    queryKey: ['top-bar-configuration'],
    queryFn: async () => {
      const res = await api.get<{ data: ISystemTopBarConfigurations }>('/system/configurations/top-bar')
      return res.data
    }
  })

  const mutation = useMutation({
    mutationFn: async (data: Pick<ISystemTopBarConfigurations, "is_active" | "mobile_content" | "desktop_content" | "cta_url">) => {
      const res = await api.put('/system/configurations/top-bar', data)
      return res.data
    },
    onSuccess: () => {
      showSnackbar('Top bar configurations updated successfully', 'success')
    },
    onError: () => {
      showSnackbar('Failed to update top bar configurations', 'error')
    }
  })

  const methods = useForm<Pick<ISystemTopBarConfigurations, "is_active" | "mobile_content" | "desktop_content" | "cta_url">>({
    defaultValues: {
      mobile_content: '',
      desktop_content: '',
      cta_url: '',
      is_active: true,
    },
    resolver: yupResolver(yup.object({
      mobile_content: yup.string().label('Mobile Content').required().max(TOP_BANNER_LIMITS.MOBILE_CONTENT_MAX_LENGTH),
      desktop_content: yup.string().label('Desktop Content').required().max(TOP_BANNER_LIMITS.DESKTOP_CONTENT_MAX_LENGTH),
      cta_url: yup.string().label('URL Link').required().url(),
      is_active: yup.boolean().label('Active').required(),
    })),
    mode: 'all',
  })

  const { handleSubmit } = methods

  const handleSubmitForm = handleSubmit((data) => {
    mutation.mutate(data)
  })

  useEffect(() => {
    if (query.data) {
      const data = query.data.data

      methods.reset({
        mobile_content: data.mobile_content,
        desktop_content: data.desktop_content,
        cta_url: data.cta_url,
        is_active: data.is_active,
      })
    }
  }, [query.data])

  return (
    <FormProvider {...methods} >

      <SystemBackdrop isOpen={query.isLoading}>
        <WrappedLoader loaderSize='lg' loaderColor='white' />
      </SystemBackdrop>

      <FormWrapper
        formOptions={{
          formSize: '1/2',
        }}
        submitOptions={{
          handleSubmit: handleSubmitForm,
        }}
      >
        <ControlledSystemTextInput
          name="mobile_content"
          // label="Mobile Content"
          description={`Mobile Content (limited to ${TOP_BANNER_LIMITS.MOBILE_CONTENT_MAX_LENGTH} characters with spaces)`}
          multiline
          rows={5}
        />

        <ControlledSystemTextInput
          name="desktop_content"
          // label="Desktop Content"
          description={`Desktop Content (limited to ${TOP_BANNER_LIMITS.DESKTOP_CONTENT_MAX_LENGTH} characters with spaces)`}
          multiline
          rows={5}
        />

        <ControlledSystemTextInput
          name="cta_url"
          description="URL Link"
        />

        <div
          className='self-end'
        >
          <ControlledSystemSwitch
            label='Active'
            name='is_active'
          />
        </div>

        <FormActions
          isFormLoading={query.isLoading || mutation.isPending}
          submitOptions={{
            label: 'Update'
          }}
        />
      </FormWrapper>
    </FormProvider>
  )
}

const BannersConfiguration = () => {
  const { showSnackbar } = useSnackbar()

  const query = useQuery({
    queryKey: ['banners-configuration'],
    queryFn: async () => {
      const res = await api.get<{ data: ISystemBannersConfigurations }>('/system/configurations/banners')
      return res.data
    }
  })

  const mutation = useMutation({
    mutationFn: async (data: Pick<ISystemBannersConfigurations, "news_landing_page_banners" | "research_landing_page_banners" | "page_popup_banners" | "story_page_banners">) => {
      const res = await api.put('/system/configurations/banners', data)
      return res.data
    },
    onSuccess: () => {
      showSnackbar('Banners configurations updated successfully', 'success')
    },
    onError: () => {
      showSnackbar('Failed to update banners configurations', 'error')
    }
  })

  const methods = useForm<Pick<ISystemBannersConfigurations, "news_landing_page_banners" | "research_landing_page_banners" | "page_popup_banners" | "story_page_banners">>({
    defaultValues: {
      news_landing_page_banners: {
        // is_active: true,
        desktop_banner_asset: '',
        mobile_banner_asset: '',
      },
      research_landing_page_banners: {
        // is_active: true,
        desktop_banner_asset: '',
        mobile_banner_asset: '',
      },
      page_popup_banners: {
        // is_active: true,
        desktop_banner_asset: '',
        mobile_banner_asset: '',
      },
      story_page_banners: {
        // is_active: true,
        desktop_banner_asset: '',
        mobile_banner_asset: '',
      },
    },
    resolver: yupResolver(
      yup.object({
        news_landing_page_banners: yup.object({
          desktop_banner_asset: yup.string().label('News Landing Page Desktop Banner URL').required(),
          mobile_banner_asset: yup.string().label('News Landing Page Mobile Banner URL').required(),
          cta_url: yup.string().label('News Landing Page Banner URL Link').required().url(),
          // is_active: yup.boolean().label('News Landing Page Banner Active').required(),
        }).label('News Landing Page Banners').required(),

        research_landing_page_banners: yup.object({
          desktop_banner_asset: yup.string().label('Research Landing Page Desktop Banner URL').required(),
          mobile_banner_asset: yup.string().label('Research Landing Page Mobile Banner URL').required(),
          cta_url: yup.string().label('Research Landing Page Banner URL Link').required().url(),
          // is_active: yup.boolean().label('Research Landing Page Banner Active').required(),
        }).label('Research Landing Page Banners').required(),

        page_popup_banners: yup.object({
          desktop_banner_asset: yup.string().label('Page Popup Desktop Banner URL').required(),
          mobile_banner_asset: yup.string().label('Page Popup Mobile Banner URL').required(),
          cta_url: yup.string().label('Page Popup Banner URL Link').required().url(),
          // is_active: yup.boolean().label('Page Popup Banner Active').required(),
        }).label('Page Popup Banners').required(),

        story_page_banners: yup.object({
          desktop_banner_asset: yup.string().label('Story Page Desktop Banner URL').required(),
          mobile_banner_asset: yup.string().label('Story Page Mobile Banner URL').required(),
          cta_url: yup.string().label('Story Page Banner URL Link').required().url(),
          // is_active: yup.boolean().label('Story Page Banner Active').required(),
        }).label('Story Page Banners').required(),
      }))
  })

  const { handleSubmit, watch } = methods

  const [newsLandingPageBanners, researchLandingPageBanners, pagePopupBanners, storyPageBanners] = watch(['news_landing_page_banners', 'research_landing_page_banners', 'page_popup_banners', 'story_page_banners'])

  const handleSubmitForm = handleSubmit((data) => {
    mutation.mutate(data)
  })

  useEffect(() => {
    if (query.data) {
      const data = query.data.data

      methods.reset({
        news_landing_page_banners: data.news_landing_page_banners,
        research_landing_page_banners: data.research_landing_page_banners,
        page_popup_banners: data.page_popup_banners,
        story_page_banners: data.story_page_banners,
      })
    }
  }, [query.data])

  return (
    <FormProvider {...methods} >

      <SystemBackdrop isOpen={query.isLoading}>
        <WrappedLoader loaderSize='lg' loaderColor='white' />
      </SystemBackdrop>

      <FormWrapper
        formOptions={{
          formSize: '1/2',
        }}
        submitOptions={{
          handleSubmit: handleSubmitForm,
        }}
      >

        <div className='grid grid-cols-2 gap-4'>
          <p className='col-span-2'>
            DL News Homepage banners
          </p>

          <ControlledAssetInput
            name='news_landing_page_banners.desktop_banner_asset'
            // initialValue={null}
            valueKey='src_url'
            initialValue={newsLandingPageBanners.desktop_banner_asset ? { _id: '', src_url: newsLandingPageBanners.desktop_banner_asset } as TAssetMetadata : null}
            mainAction={{
              show: true,
              actionText: "Desktop (2745x380)"
            }}
            showFullPreview
          />

          <ControlledAssetInput
            name='news_landing_page_banners.mobile_banner_asset'
            // initialValue={null}
            valueKey='src_url'
            initialValue={newsLandingPageBanners.mobile_banner_asset ? { _id: '', src_url: newsLandingPageBanners.mobile_banner_asset } as TAssetMetadata : null}
            mainAction={{
              show: true,
              actionText: "Mobile (600x300)"
            }}
            showFullPreview
          />

          <div className='col-span-2 mt-4'>
            <ControlledSystemTextInput
              name="news_landing_page_banners.cta_url"
              description="URL Link"
            />
          </div>
        </div>

        <div className='grid grid-cols-2 gap-4 mt-6'>
          <p className='col-span-2'>
            DL News Homepage pop-up banners
          </p>

          <ControlledAssetInput
            name='page_popup_banners.desktop_banner_asset'
            // initialValue={null}
            valueKey='src_url'
            initialValue={pagePopupBanners.desktop_banner_asset ? { _id: '', src_url: pagePopupBanners.desktop_banner_asset } as TAssetMetadata : null}
            mainAction={{
              show: true,
              actionText: "Desktop (2745x760)"
            }}
            showFullPreview
          />

          <ControlledAssetInput
            name='page_popup_banners.mobile_banner_asset'
            // initialValue={null}
            valueKey='src_url'
            initialValue={pagePopupBanners.mobile_banner_asset ? { _id: '', src_url: pagePopupBanners.mobile_banner_asset } as TAssetMetadata : null}
            mainAction={{
              show: true,
              actionText: "Mobile (410x440)"
            }}
            showFullPreview
          />

          <div className='col-span-2 mt-4'>
            <ControlledSystemTextInput
              name="page_popup_banners.cta_url"
              description="URL Link"
            />
          </div>
        </div>

        <div className='grid grid-cols-2 gap-4 mt-6'>
          <p className='col-span-2'>
            Research Landing Page banners
          </p>

          <ControlledAssetInput
            name='research_landing_page_banners.desktop_banner_asset'
            // initialValue={null}
            valueKey='src_url'
            initialValue={researchLandingPageBanners.desktop_banner_asset ? { _id: '', src_url: researchLandingPageBanners.desktop_banner_asset } as TAssetMetadata : null}
            mainAction={{
              show: true,
              actionText: "Desktop (2745x380)"
            }}
            showFullPreview
          />

          <ControlledAssetInput
            name='research_landing_page_banners.mobile_banner_asset'
            // initialValue={null}
            valueKey='src_url'
            initialValue={researchLandingPageBanners.mobile_banner_asset ? { _id: '', src_url: researchLandingPageBanners.mobile_banner_asset } as TAssetMetadata : null}
            mainAction={{
              show: true,
              actionText: "Mobile (600x300)"
            }}
            showFullPreview
          />

          <div className='col-span-2'>
            <ControlledSystemTextInput
              name="research_landing_page_banners.cta_url"
              description="URL Link"
            />
          </div>
        </div>

        <div className='grid grid-cols-2 gap-4 mt-6'>
          <p className='col-span-2'>
            Article Page banners
          </p>

          <ControlledAssetInput
            name='story_page_banners.desktop_banner_asset'
            // initialValue={null}
            valueKey='src_url'
            initialValue={storyPageBanners.desktop_banner_asset ? { _id: '', src_url: storyPageBanners.desktop_banner_asset } as TAssetMetadata : null}
            mainAction={{
              show: true,
              actionText: "Desktop (526x844)"
            }}
            showFullPreview
          />

          <ControlledAssetInput
            name='story_page_banners.mobile_banner_asset'
            // initialValue={null}
            valueKey='src_url'
            initialValue={storyPageBanners.mobile_banner_asset ? { _id: '', src_url: storyPageBanners.mobile_banner_asset } as TAssetMetadata : null}
            mainAction={{
              show: true,
              actionText: "Mobile (600x300)"
            }}
            showFullPreview
          />

          <div className='col-span-2 mt-4'>
            <ControlledSystemTextInput
              name="story_page_banners.cta_url"
              description="URL Link"
            />
          </div>
        </div>

        <FormActions
          isFormLoading={query.isLoading || mutation.isPending}
          submitOptions={{
            label: 'Update'
          }}
        />
      </FormWrapper>
    </FormProvider>
  )
}

const DLRXPostsConfiguration = () => {
  const { showSnackbar } = useSnackbar()

  const NUM_POSTS = 10

  type FormData = Record<`x_post_id_${number}`, string>

  const query = useQuery({
    queryKey: ['dlr-x-posts-configuration'],
    queryFn: async () => {
      const res = await api.get<{ data: ISystemDLRXPostsConfigurations }>('/system/configurations/dlr-x-posts')
      return res.data
    }
  })

  const mutation = useMutation({
    mutationFn: async (data: FormData) => {
      // Transform form data to backend format (array)
      const x_post_ids = Array.from({ length: NUM_POSTS }, (_, i) => data[`x_post_id_${i + 1}`])
        .filter(id => id && id.trim() !== '') // Remove empty IDs

      const res = await api.put('/system/configurations/dlr-x-posts', { x_post_ids })
      return res.data
    },
    onSuccess: () => {
      showSnackbar('DLR X posts configurations updated successfully', 'success')
    },
    onError: () => {
      showSnackbar('Failed to update DLR X posts configurations', 'error')
    }
  })

  // Generate default values
  const defaultValues = Array.from({ length: NUM_POSTS }, (_, i) => [`x_post_id_${i + 1}`, ''])
    .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})

  // Generate validation schema
  const validationSchema = Array.from({ length: NUM_POSTS }, (_, i) => {
    const fieldName = `x_post_id_${i + 1}`
    const label = `X Post ID ${i + 1}`
    return [
      fieldName,
      yup.string().label(label).test('is-valid-id', `${label} must be a valid X post ID (numbers only)`, function (value) {
        if (!value || value.trim() === '') return true;
        return /^\d+$/.test(value.trim());
      })
    ] as [string, any]
  }).reduce((acc, [key, schema]) => ({ ...acc, [key]: schema }), {} as Record<string, any>)

  const methods = useForm<FormData>({
    defaultValues,
    resolver: yupResolver(yup.object(validationSchema))
  })

  const { handleSubmit } = methods

  const handleSubmitForm = handleSubmit((data) => {
    mutation.mutate(data)
  })

  useEffect(() => {
    if (query.data?.data) {
      const data = query.data.data

      // Transform backend data (array) to form format
      const xPostIds = data.x_post_ids || []
      const formData = Array.from({ length: NUM_POSTS }, (_, i) => [`x_post_id_${i + 1}`, xPostIds[i] || ''])
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})

      methods.reset(formData)
    }
  }, [query.data])

  return (
    <FormProvider {...methods} >
      <SystemBackdrop isOpen={query.isLoading}>
        <WrappedLoader loaderSize='lg' loaderColor='white' />
      </SystemBackdrop>


      <FormWrapper
        formOptions={{
          formSize: '1/2',
        }}
        submitOptions={{
          handleSubmit: handleSubmitForm,
        }}
      >
        <div className='grid grid-cols-2 gap-4'>
          {Array.from({ length: NUM_POSTS }, (_, i) => {
            const fieldNumber = i + 1
            return (
              <ControlledSystemTextInput
                key={`x_post_id_${fieldNumber}`}
                name={`x_post_id_${fieldNumber}`}
                description={`X Post ID ${fieldNumber}`}
              />
            )
          })}
        </div>

        <FormActions
          isFormLoading={query.isLoading || mutation.isPending}
          submitOptions={{
            label: 'Update'
          }}
        />
      </FormWrapper>
    </FormProvider>
  )
}

const DLRMentionsConfiguration = () => {
  const { showSnackbar } = useSnackbar()

  const NUM_BLOCKS = 3
  const NUM_QUOTES = 3

  type QuoteFormData = {
    quote: string;
    username: string;
    social_media_name: string;
    social_media_link: string;
    x_post_id: string;
  }

  type BlockFormData = {
    is_active: boolean;
    quotes: QuoteFormData[];
  }

  type FormData = {
    blocks: BlockFormData[];
  }

  const query = useQuery({
    queryKey: ['dlr-mentions-configuration'],
    queryFn: async () => {
      const res = await api.get<{ data: ISystemDLRMentionsConfigurations }>('/system/configurations/dlr-mentions')
      return res.data
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: Infinity,
  })

  const mutation = useMutation({
    mutationFn: async (data: FormData) => {
      const res = await api.put('/system/configurations/dlr-mentions', data)
      return res.data
    },
    onSuccess: () => {
      showSnackbar('DLR Mentions configurations updated successfully', 'success')
    },
    onError: () => {
      showSnackbar('Failed to update DLR Mentions configurations', 'error')
    }
  })

  // Generate default values
  const defaultValues: FormData = {
    blocks: Array.from({ length: NUM_BLOCKS }, () => ({
      is_active: false,
      quotes: Array.from({ length: NUM_QUOTES }, () => ({
        quote: '',
        username: '',
        social_media_name: '',
        social_media_link: '',
        x_post_id: '',
      }))
    }))
  }

  // Generate validation schema
  const quoteSchema = yup.object({
    quote: yup.string().label('Quote'),
    username: yup.string().label('Username'),
    social_media_name: yup.string().label('Social Media Name'),
    social_media_link: yup.string().label('Social Media Link').test('is-url', 'Must be a valid URL', function (value) {
      if (!value || value.trim() === '') return true;
      return yup.string().url().isValidSync(value);
    }),
    x_post_id: yup.string().label('X Post ID').test('is-valid-id', 'Must be a valid X post ID (numbers only)', function (value) {
      if (!value || value.trim() === '') return true;
      return /^\d+$/.test(value.trim());
    }),
  })

  const validationSchema = yup.object({
    blocks: yup.array().of(
      yup.object({
        is_active: yup.boolean().required(),
        quotes: yup.array().of(quoteSchema).required()
      })
    ).required()
  }) as any

  const methods = useForm<FormData>({
    defaultValues,
    resolver: yupResolver(validationSchema)
  })

  const { handleSubmit, watch } = methods

  const handleSubmitForm = handleSubmit((data) => {
    mutation.mutate(data)
  })

  useEffect(() => {
    if (query.data?.data) {
      const data = query.data.data

      if (data.blocks && data.blocks.length > 0) {
        methods.reset({
          blocks: data.blocks.map((block) => ({
            is_active: block.is_active || false,
            quotes: Array.from({ length: NUM_QUOTES }, (_, quoteIndex) => {
              const quote = block.quotes?.[quoteIndex]
              return {
                quote: quote?.quote || '',
                username: quote?.username || '',
                social_media_name: quote?.social_media_name || '',
                social_media_link: quote?.social_media_link || '',
                x_post_id: quote?.x_post_id || '',
              }
            })
          }))
        })
      }
    }
  }, [query.data])

  const [activeBlockTab, setActiveBlockTab] = useState(0)

  const blockTabs = Array.from({ length: NUM_BLOCKS }, (_, i) => ({
    label: `Block ${i + 1}`,
    content: (
      <div key={`block-${i}`} className="space-y-6">
        <div className='grid grid-cols-3 gap-4'>
          {Array.from({ length: NUM_QUOTES }, (_, j) => {
            const quoteValue = watch(`blocks.${i}.quotes.${j}.quote`) || ''
            const characterCount = quoteValue.length
            const maxChars = j === 0 ? 45 : 160

            return (
              <div key={`quote-${j}`} className='space-y-4'>
                <ControlledSystemTextInput
                  name={`blocks.${i}.quotes.${j}.quote`}
                  description={`Quote ${j + 1}${j === 0 ? ' (small)' : ' (big)'}`}
                  multiline
                  rows={3}
                />
                <p className={`text-sm ${characterCount > maxChars ? 'text-red-500' : 'text-gray-500'}`}>
                  {characterCount} / {maxChars} characters
                </p>
                {j === 0 && (
                  <p className='text-sm text-gray-500'>
                    Maximum length is approximately 45 characters for small quote
                  </p>
                )}
                {(j === 1 || j === 2) && (
                  <p className='text-sm text-gray-500'>
                    Maximum length is approximately 160 characters for big quote
                  </p>
                )}

                <ControlledSystemTextInput
                  name={`blocks.${i}.quotes.${j}.username`}
                  description={`Username ${j + 1}`}
                />

                <ControlledSystemTextInput
                  name={`blocks.${i}.quotes.${j}.social_media_name`}
                  description={`Social Media Name ${j + 1}`}
                />

                <ControlledSystemTextInput
                  name={`blocks.${i}.quotes.${j}.social_media_link`}
                  description={`Social Media Link ${j + 1}`}
                />

                <ControlledSystemTextInput
                  name={`blocks.${i}.quotes.${j}.x_post_id`}
                  description={`X Post ID ${j + 1}`}
                />
              </div>
            )
          })}
        </div>

        <div className='flex justify-end'>
          <ControlledSystemSwitch
            label='Active'
            name={`blocks.${i}.is_active`}
          />
        </div>
      </div>
    )
  }))

  return (
    <FormProvider {...methods}>
      <SystemBackdrop isOpen={query.isLoading}>
        <WrappedLoader loaderSize='lg' loaderColor='white' />
      </SystemBackdrop>

      <FormWrapper
        formOptions={{
          formSize: 'full',
        }}
        submitOptions={{
          handleSubmit: handleSubmitForm,
        }}
      >
        <SystemMultiTab
          tabs={blockTabs}
          activeIndex={activeBlockTab}
          handleChange={setActiveBlockTab}
          labelOptions={{
            containerClassName: 'bg-transparent'
          }}
        />

        <FormActions
          isFormLoading={query.isLoading || mutation.isPending}
          submitOptions={{
            label: 'Update'
          }}
        />
      </FormWrapper>
    </FormProvider>
  )
}

const TABS = [
  {
    label: 'Top Bar',
    content: (
      <TopBarConfiguration />
    ),
  },
  {
    label: 'Banners',
    content: (
      <BannersConfiguration />
    ),
  },
  {
    label: 'DLR X posts',
    content: (
      <DLRXPostsConfiguration />
    ),
  },
  {
    label: 'DLR Mentions on Social Media',
    content: (
      <DLRMentionsConfiguration />
    ),
  }
];

const ConfigurationsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const activeTabParam = searchParams.get('tab')

  const [activeTabIndex, setActiveTabIndex] = useState(0)

  const handleChange = (index: number) => {
    setSearchParams((prevValue) => {
      prevValue.set('tab', String(index))
      return prevValue
    })
  }

  useEffect(() => {
    if (activeTabParam) {
      const tabIndex = Number(activeTabParam)

      if (tabIndex >= 0 && tabIndex < TABS.length) {
        setActiveTabIndex(tabIndex)
      }
      else {
        setActiveTabIndex(0)
      }
    }
  }, [activeTabParam])

  return (
    <PageContainer
      title={renderRouteOptions(Routes.CONFIGURATIONS).title}
    >
      <SystemMultiTab
        tabs={TABS}
        activeIndex={activeTabIndex}
        handleChange={handleChange}
      />
    </PageContainer>
  )
}

export default ConfigurationsPage